import { useCallback, useEffect, useState } from "react";
import { Shuffle } from "lucide-react";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { getRandomDiscovery } from "@/lib/content.functions";

interface Props {
  variant?: "icon" | "pill";
  className?: string;
}

/**
 * Sends the visitor to a random published discovery. Powers the
 * "Surprise me" header button and the `r` keyboard shortcut.
 */
export function SurpriseButton({ variant = "icon", className = "" }: Props) {
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [loading, setLoading] = useState(false);

  const currentSlug = pathname.startsWith("/discovery/")
    ? decodeURIComponent(pathname.slice("/discovery/".length))
    : undefined;

  const go = useCallback(async () => {
    if (loading) return;
    setLoading(true);
    try {
      const pick = await getRandomDiscovery({ data: { excludeSlug: currentSlug } });
      if (pick) {
        navigate({ to: "/discovery/$slug", params: { slug: pick.slug } });
      }
    } finally {
      setLoading(false);
    }
  }, [currentSlug, loading, navigate]);

  // Keyboard shortcut: `r` (when not typing in a field) jumps to a random discovery.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "r" && e.key !== "R") return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const el = e.target as HTMLElement | null;
      if (el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable)) return;
      e.preventDefault();
      void go();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go]);

  if (variant === "pill") {
    return (
      <button
        type="button"
        onClick={go}
        disabled={loading}
        title="Surprise me - random discovery (press R)"
        className={`plausible-event-name=Surprise+Me inline-flex items-center gap-1.5 rounded-full border border-border bg-background/40 px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest text-foreground transition hover:border-primary/60 hover:text-primary disabled:opacity-60 ${className}`}
      >
        <Shuffle className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
        Surprise me
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={go}
      disabled={loading}
      aria-label="Surprise me - random discovery"
      title="Surprise me (R)"
      className={`plausible-event-name=Surprise+Me inline-flex h-11 w-11 items-center justify-center rounded-full text-muted-foreground transition hover:bg-muted hover:text-foreground disabled:opacity-60 ${className}`}
    >
      <Shuffle className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
    </button>
  );
}
